import { type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Boxes } from 'lucide-react';
import { useApiClient } from '../../lib/api/api-context.js';
import { getReadUrl } from '../../lib/media/media-api.js';

/**
 * The picture on a heading.
 *
 * A media id rather than a URL, for the same reason the board's card thumbnail is: read URLs are
 * signed and short-lived, while this screen is left open. So the URL is fetched per row that is
 * actually on screen, and the crate icon stands in — permanently, for the many headings a shop
 * never photographs, which must not look like a broken image.
 */
export function StockGroupImage({
  mediaId,
  alt,
}: {
  mediaId: string | null;
  alt: string;
}): ReactElement {
  const client = useApiClient();

  const readUrl = useQuery({
    queryKey: ['media', mediaId, 'url', 'thumb'],
    queryFn: () => getReadUrl(client, mediaId as string, 'thumb'),
    enabled: mediaId !== null,
    gcTime: 0,
    staleTime: 60_000,
    retry: 1,
  });

  if (mediaId && readUrl.data) {
    return (
      <img
        src={readUrl.data.url}
        alt={alt}
        loading="lazy"
        className="size-12 shrink-0 rounded-md object-cover"
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className="flex size-12 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
    >
      <Boxes className="size-5" />
    </span>
  );
}
